import React, { useState, useEffect } from 'react';
import { Container, Grid, Typography, Box, CircularProgress, Modal, Button } from '@mui/material';
import PoseCard from './components/PoseCard';
import Camera from './components/Camera';
import { analyzeVideo } from './utils/api';

// Exercise data mapping to backend exercise types
const EXERCISES = [
  {
    id: 'jaw_opening_and_closing',
    name: 'Jaw Opening/Closing',
    description: 'Slowly open and close your jaw in a controlled motion.',
    image: '/assets/jawopenclose.png',
  },
  {
    id: 'puckering',
    name: 'Lip Puckering',
    description: 'Pucker your lips as if you\'re about to whistle or blow a kiss.',
    image: '/assets/puckering.png',
  },
  {
    id: 'smacking',
    name: 'Lip Smacking',
    description: 'Press your lips together firmly and then release with a smacking sound.',
    image: '/assets/lipsmack.png',
  },
  {
    id: 'smiling',
    name: 'Lip Stretching',
    description: 'Smile widely, stretching your lips as far as comfortable.',
    image: '/assets/smiling.png',
  },
];

function App() {
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [currentPose, setCurrentPose] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleStartRecording = (exerciseId) => {
    const exercise = EXERCISES.find(e => e.id === exerciseId);
    setCurrentPose(exercise);
    setIsCameraOpen(true);
  };

  const [analysisResult, setAnalysisResult] = useState(null);

  // Function to play text-to-speech feedback
  const playFeedback = (text, isGoodScore = true) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = isGoodScore ? 1.2 : 0.9; // Higher pitch for good scores
    utterance.volume = 1.0;
    window.speechSynthesis.speak(utterance);
  };

  const handleRecordingComplete = async (videoFile) => {
    console.log('=== HANDLE RECORDING COMPLETE ===');
    console.log('Exercise Type:', currentPose?.id);
    
    // Track if the component is still mounted
    let isMounted = true;
    
    // Close recording modal immediately
    setIsCameraOpen(false);
    
    // Log the received file details
    try {
      console.log('Video file received:', {
        name: videoFile?.name,
        size: videoFile?.size,
        type: videoFile?.type,
        isFile: videoFile instanceof File,
        isBlob: videoFile instanceof Blob,
        constructor: videoFile?.constructor?.name
      });
    } catch (e) {
      console.error('Error logging file details:', e);
      return; // Don't proceed if we can't log the file details
    }
    
    // Validate inputs
    if (!videoFile) {
      const errorMsg = 'No video file provided';
      console.error(errorMsg);
      alert(errorMsg);
      return;
    }
    
    if (!currentPose || !currentPose.id) {
      const errorMsg = 'No exercise type selected';
      console.error(errorMsg);
      alert(errorMsg);
      return;
    }
    
    if (!EXERCISES.some(e => e.id === currentPose.id)) {
      const errorMsg = `Invalid exercise type: ${currentPose.id}. Must be one of: ${EXERCISES.map(e => e.id).join(', ')}`;
      console.error(errorMsg);
      alert(errorMsg);
      return;
    }
    
    if (videoFile.size === 0) {
      const errorMsg = 'Video file is empty (0 bytes)';
      console.error(errorMsg);
      alert(errorMsg);
      return;
    }
    
    console.log('=== VIDEO FILE RECEIVED ===');
    console.log('Name:', videoFile.name);
    console.log('Size:', videoFile.size, 'bytes');
    console.log('Type:', videoFile.type);
    console.log('Is File instance:', videoFile instanceof File);
    console.log('Is Blob instance:', videoFile instanceof Blob);
    
    setIsProcessing(true);
    
    try {
      // Create form data with the video file and exercise type
      const formData = new FormData();
      formData.append('file', videoFile, 'exercise.webm');
      formData.append('exercise_type', currentPose.id); // Use currentPose.id directly
      
      console.log('=== SENDING TO BACKEND ===');
      console.log('Exercise type:', currentPose.id);
      console.log('File name:', videoFile.name);
      
      const response = await analyzeVideo(formData);
      
      // Don't update state if component is unmounted
      if (!isMounted) return;
      
      console.log('=== BACKEND RESPONSE ===');
      console.log('Response:', response);
      
      if (response && response.status === 'success') {
        console.log('=== ANALYSIS SUCCESS ===');
        console.log('Raw backend response:', response);
        console.log('Response type:', typeof response);
        
        // Parse response if it's a string
        const resultData = typeof response === 'string' ? JSON.parse(response) : response;
        console.log('Parsed response data:', resultData);
        
        // Store the analysis result
        const analysisData = {
          status: resultData.status,
          exercise_type: resultData.exercise_type,
          exerciseType: resultData.exercise_type,
          analysis: resultData.analysis,
          score: resultData.score,
          feedback: resultData.feedback,
          timestamp: new Date().toISOString()
        };
        
        console.log('Setting analysis result:', analysisData);
        setAnalysisResult(analysisData);
        
        // Check score and provide appropriate feedback
        if (analysisData.score) {
          const numericScore = typeof analysisData.score === 'string' 
            ? parseInt(analysisData.score.split('/')[0], 10)
            : analysisData.score;
          
          if (numericScore >= 5) {
            playFeedback('Great job! Your exercise form is excellent. Keep up the good work!', true);
          } else {
            playFeedback('Try again. Focus on following the exercise instructions carefully.', false);
          }
        }
        
        // Show completion message
        alert('Video analysis completed!');
      } else {
        throw new Error(response?.message || 'Unknown error from server');
      }
    } catch (error) {
      console.error('Error processing video file:', error);
      if (isMounted) {
        alert(`Error processing video: ${error.message}`);
        alert(`Error: ${error.response.status} - ${error.response.data?.detail || 'Unknown error'}`);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('No response received:', error.request);
        alert('No response from server. Please check your connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Request setup error:', error.message);
        alert(`Error: ${error.message}`);
      }
    } finally {
      console.log('=== CLEANING UP ===');
      setIsProcessing(false);
      setCurrentPose(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ 
        py: 6,
        px: { xs: 2, sm: 4 },
        minHeight: '100vh',
        position: 'relative',
        zIndex: 1,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 20,
          left: 20,
          right: 20,
          bottom: 20,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderRadius: 4,
          zIndex: -1
        }
      }}>
      <Box 
        textAlign="center" 
        mb={6}
        sx={{
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-20px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '120px',
            height: '4px',
            background: 'linear-gradient(45deg, #2196F3, #1976D2)',
            borderRadius: '2px',
          }
        }}
      >
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(45deg, #1976D2, #2196F3)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px',
            mb: 3
          }}
        >
          Pose Detector
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'text.secondary',
            maxWidth: '600px',
            margin: '0 auto',
            fontWeight: 400
          }}
        >
          Select an exercise to begin your orofacial motor practice session
        </Typography>
      </Box>

      <Grid 
        container 
        spacing={4} 
        justifyContent="center"
        sx={{
          px: { xs: 2, sm: 4 },
          pb: 6
        }}
      >
        {EXERCISES.map((exercise) => (
          <Grid 
            item 
            key={exercise.id} 
            xs={12} 
            sm={6} 
            md={4} 
            lg={3}
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <PoseCard
              pose={exercise}
              onStartRecording={handleStartRecording}
            />
          </Grid>
        ))}
      </Grid>

      {/* Analysis Result Modal */}
      <Modal 
        open={!!analysisResult} 
        onClose={() => setAnalysisResult(null)}
        aria-labelledby="analysis-result-modal"
        aria-describedby="analysis-result-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 800,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          p: { xs: 2, sm: 4 },
          borderRadius: 4,
          maxHeight: '90vh',
          overflowY: 'auto',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.12)'
          }
        }}>
          <Typography 
            variant="h5" 
            component="h2" 
            sx={{
              fontWeight: 600,
              background: 'linear-gradient(45deg, #2196F3, #1976D2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 3
            }}
          >
            Analysis Results: {analysisResult?.exercise_type || 'Exercise'}
          </Typography>
          
          {/* Score Section */}
          {analysisResult?.score && (
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Score:
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  background: 'linear-gradient(45deg, #2196F3, #1976D2)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 'bold',
                  fontSize: '1.5rem'
                }}
              >
                {typeof analysisResult.score === 'string' 
                  ? analysisResult.score.split('/')[0] + '/10'
                  : `${analysisResult.score}/10`
                }
              </Typography>
            </Box>
          )}
          
          {/* Main Analysis Section */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                background: 'linear-gradient(45deg, #2196F3, #1976D2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 600,
                mb: 2,
                display: 'inline-block'
              }}
            >
              Detailed Analysis:
            </Typography>
            <Typography variant="body1" component="div" 
              sx={{ 
                whiteSpace: 'pre-wrap', 
                mt: 2,
                p: 3,
                backgroundColor: 'rgba(33, 150, 243, 0.02)',
                borderRadius: 3,
                border: '1px solid rgba(33, 150, 243, 0.1)',
                boxShadow: '0 4px 16px rgba(33, 150, 243, 0.05)',
                lineHeight: 1.6
              }}
            >
              {(() => {
                // Helper function to render nested objects
                const renderValue = (value) => {
                  if (typeof value === 'object' && value !== null) {
                    return Object.entries(value).map(([subKey, subValue], index) => (
                      <Box key={subKey} sx={{ ml: 2, mb: 1 }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: 500,
                          color: 'text.secondary',
                          textTransform: 'capitalize'
                        }}>
                          {subKey.replace(/_/g, ' ')}:
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {typeof subValue === 'object' ? renderValue(subValue) : subValue}
                        </Typography>
                      </Box>
                    ));
                  }
                  return value;
                };

                // Main render logic
                if (typeof analysisResult?.analysis === 'object') {
                  return Object.entries(analysisResult.analysis).map(([key, value]) => (
                    <Box key={key} sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 600,
                        color: 'text.primary',
                        textTransform: 'capitalize',
                        mb: 1,
                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                        pb: 0.5
                      }}>
                        {key.replace(/_/g, ' ')}
                      </Typography>
                      {renderValue(value)}
                    </Box>
                  ));
                }
                return analysisResult?.analysis || 'No analysis results available.';
              })()}
              
            </Typography>
          </Box>
          
          {/* Feedback Section */}
          {analysisResult?.feedback && (
            <Box sx={{ 
              mt: 3, 
              p: 3, 
              background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.03) 0%, rgba(33, 150, 243, 0.07) 100%)', 
              borderRadius: 3,
              border: '1px solid rgba(33, 150, 243, 0.1)',
              boxShadow: '0 4px 12px rgba(33, 150, 243, 0.05)'
            }}>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Additional Feedback:
              </Typography>
              <Typography variant="body1">
                {analysisResult.feedback}
              </Typography>
            </Box>
          )}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              onClick={() => setAnalysisResult(null)}
              sx={{
                borderRadius: 3,
                py: 1.5,
                px: 4,
                background: 'linear-gradient(45deg, #2196F3, #1976D2)',
                boxShadow: '0 4px 16px rgba(33, 150, 243, 0.3)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1976D2, #1565C0)',
                  boxShadow: '0 6px 20px rgba(33, 150, 243, 0.4)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Camera
        isOpen={isCameraOpen}
        onClose={() => {
          setIsCameraOpen(false);
          setCurrentPose(null);
        }}
        onRecordingComplete={handleRecordingComplete}
        exercise={currentPose}
      />

      {isProcessing && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              p: 4,
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">Processing your video...</Typography>
            <Typography variant="body2" color="text.secondary">
              Analyzing your {currentPose?.name} pose
            </Typography>
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default App;
